import { useState } from "react";
import { api } from "@/api/client";
import { PageHeader } from "@/components/common";
import { Send, Bot, User as UserIcon, Loader2 } from "lucide-react";

const SUGGESTED = [
  "What tasks are delayed because of approval?",
  "Show me Raj's repeated delay reasons.",
  "Which team member is overloaded?",
  "Summarize this month's team performance.",
  "Which recurring tasks are failing regularly?",
];

export default function MemoryChat() {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm SKIVERSE Task App. Ask me anything about your saved memories, tasks, approvals, or team performance." }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  const send = async (text) => {
    const q = text || input;
    if (!q.trim()) return;
    setMessages(m => [...m, { role: "user", content: q }]);
    setInput("");
    setLoading(true);
    try {
      const r = await api.post("/memories/chat", { question: q });
      const ans = r.data?.data;
      setMessages(m => [...m, {
        role: "assistant",
        content: ans?.answer || "No answer.",
        sources: ans?.sources || [],
        sources_count: ans?.sources_count
      }]);
    } catch (e) {
      setMessages(m => [...m, { role: "assistant", content: "Sorry, I couldn't process that." }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-7rem)]" data-testid="ai-chat-page">
      <PageHeader title="AI Chat" breadcrumbs={["AI Memory", "Chat"]}/>

      <div className="flex-1 card flex flex-col overflow-hidden">
        <div className="card-head"><h3><span className="dot" />AI Assistant</h3><span className="text-xs text-slate-500">Memory · tasks · performance</span></div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
              {m.role !== "user" && (
                <div className="w-8 h-8 rounded-sm bg-slate-900 flex items-center justify-center text-white flex-shrink-0">
                  <Bot size={14} />
                </div>
              )}
              <div className={`max-w-[75%] p-3 rounded-sm text-sm ${
                m.role === "user" ? "bg-slate-900 text-white" : "bg-slate-50 border border-slate-200 text-slate-800"
              }`} data-testid={`chat-msg-${i}`}>
                <div className="whitespace-pre-wrap">{m.content}</div>
                {m.sources_count !== undefined && (
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-2">
                    {m.sources_count} memories searched
                  </div>
                )}
              </div>
              {m.role === "user" && (
                <div className="w-8 h-8 rounded-sm bg-[#334E7B] flex items-center justify-center text-white flex-shrink-0">
                  <UserIcon size={14} />
                </div>
              )}
            </div>
          ))}
          {loading && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-sm bg-slate-900 flex items-center justify-center text-white"><Bot size={14}/></div>
              <div className="bg-slate-50 border border-slate-200 p-3 rounded-sm text-sm text-slate-500 inline-flex items-center gap-2">
                <Loader2 size={14} className="animate-spin"/> Thinking...
              </div>
            </div>
          )}
        </div>

        {messages.length <= 1 && (
          <div className="p-4 border-t border-slate-100">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Try asking</div>
            <div className="flex flex-wrap gap-2">
              {SUGGESTED.map((s, i) => (
                <button key={i} onClick={() => send(s)} className="text-xs bg-slate-50 hover:bg-slate-100 border border-slate-200 px-3 py-1.5 rounded-sm transition-colors" data-testid={`suggested-${i}`}>{s}</button>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={e => { e.preventDefault(); send(); }} className="border-t border-slate-200 p-3 flex gap-2">
          <input
            className="erp-input flex-1"
            placeholder="Ask about tasks, approvals, performance, memory..."
            value={input}
            onChange={e => setInput(e.target.value)}
            disabled={loading}
            data-testid="chat-input"
          />
          <button type="submit" disabled={loading || !input.trim()} className="erp-btn-primary inline-flex items-center gap-2" data-testid="chat-send-btn">
            {loading ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
            Send
          </button>
        </form>
      </div>
    </div>
  );
}
